import { Color } from '@tiptap/extension-color';
import ListItem from '@tiptap/extension-list-item';
import TextStyle from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Gapcursor from '@tiptap/extension-gapcursor';
import { Node, mergeAttributes } from '@tiptap/core';
import Heading from '@tiptap/extension-heading';
import ImageResize from 'tiptap-extension-resize-image';

const BlogTitle = Node.create({
  name: 'blogTitle',
  group: 'block',
  content: 'inline*',
  addOptions() {
    return {
      HTMLAttributes: {
        contenteditable: 'false',
      },
    }
  },
  parseHTML() {
    return [
      {
        tag: 'div[type="blogTitle"]',
        getAttrs: (node) => ({
          'data-type': node.getAttribute('data-type') || 'blog-title',
          class: node.getAttribute('class') || 'blogTitle'
        })
      }
    ]
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 
      type: 'blogTitle',
      'data-type': 'blog-title',
      class: 'blogTitle',
      contenteditable: 'false'
    }), 0]
  },
});

const BlogMeta = Node.create({
  name: 'blogMeta',
  group: 'block',
  content: 'block+',
  parseHTML() {
    return [
      {
        tag: 'div[type="blogMeta"]',
        getAttrs: (node) => ({
          'data-type': node.getAttribute('data-type') || 'blog-meta',
          class: node.getAttribute('class') || 'blogMeta'
        })
      }
    ]
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, {
      type: 'blogMeta',
      'data-type': 'blog-meta',
      class: 'blogMeta'
    }), 0]
  },
});

const BlogDate = Node.create({
  name: 'blogDate',
  group: 'block',
  content: 'inline*',
  addOptions() {
    return {
      HTMLAttributes: {
        contenteditable: 'false',
      },
    }
  },
  parseHTML() {
    return [
      {
        tag: 'div[type="blogDate"]',
        getAttrs: (node) => ({
          'data-type': node.getAttribute('data-type') || 'blog-date',
          class: node.getAttribute('class') || 'date'
        })
      }
    ]
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, {
      type: 'blogDate',
      'data-type': 'blog-date',
      class: 'date',
      contenteditable: 'false'
    }), 0]
  },
});

const BlogCategory = Node.create({
  name: 'blogCategory',
  group: 'block',
  content: 'inline*',
  addOptions() {
    return {
      HTMLAttributes: {
        contenteditable: 'false',
      },
    }
  },
  parseHTML() {
    return [
      {
        tag: 'div[type="blogCategory"]',
        getAttrs: (node) => ({
          'data-type': node.getAttribute('data-type') || 'blog-category',
          class: node.getAttribute('class') || 'category'
        })
      }
    ]
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, {
      type: 'blogCategory',
      'data-type': 'blog-category',
      class: 'category',
      contenteditable: 'false'
    }), 0]
  },
});

// Custom Heading extension to support styles
const CustomHeading = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: element => element.getAttribute('data-style'),
        renderHTML: attributes => {
          // For h2 elements, default to having a border unless explicitly set to "no-border"
          if (attributes.level === 2) {
            if (attributes.style === "no-border") {
              return {
                'data-style': "no-border"
              };
            }
            return {};
          }
          if (attributes.style) {
            return {
              'data-style': attributes.style,
            };
          }
          return {};
        },
      },
    };
  },
});

const ImageGroup = Node.create({
  name: 'imageGroup',
  group: 'block',
  content: 'image*',
  parseHTML() {
    return [
      {
        tag: 'div[data-type="imageGroup"]',
      },
    ]
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 
      'data-type': 'imageGroup',
      class: 'image-group'
    }), 0]
  },
  addCommands() {
    return {
      insertImageGroup: () => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          content: [],
        })
      },
      addImageToGroup: (attrs) => ({ commands }) => {
        return commands.insertContent({
          type: 'image',
          attrs,
        })
      },
    }
  },
  addKeyboardShortcuts() {
    return {
      'Mod-Alt-g': () => this.editor.commands.insertImageGroup(),
    }
  },
})

export const extensions = [
  Color.configure({ types: [TextStyle.name, ListItem.name] }),
  TextStyle.configure({ types: [ListItem.name] }),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
    },
  }),
  Image.configure({
    inline: false,
    allowBase64: false,
    HTMLAttributes: {
      class: 'tiptap-image',
      loading: 'lazy',
      draggable: 'true',
      contenteditable: 'false',
    },
  }),
  ImageResize.configure({
    inline: false,
    allowBase64: false,
    HTMLAttributes: {
      class: 'tiptap-image',
    },
  }),
  ImageGroup,
  Link.configure({
    openOnClick: false,
    HTMLAttributes: {
      rel: 'noopener noreferrer',
      target: '_blank',
    },
  }),
  Highlight.configure({
    multicolor: true,
  }),
  TextAlign.configure({
    types: ['heading', 'paragraph'],
  }),
  Underline,
  Gapcursor,
  Table.configure({
    resizable: true,
  }),
  TableRow,
  TableCell,
  TableHeader,
  BlogTitle,
  BlogMeta,
  BlogDate,
  BlogCategory,
  CustomHeading,
]; 